import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from '@mobi/utils/functions'
import { type BettingSection, setVisibleBettingSection } from '../Store/Workflow'
import { selectWorkflowStatus } from '../Store/Workflow/selectors'
import { hasEnoughItemsToCreateValidMulti, isSelectionAllowedInMulti } from '../helpers/state'
import type { BetSlipItem } from '../types'

export const useTabSectionObserver = (
  containerElRef: React.RefObject<HTMLDivElement | null>,
  items: BetSlipItem[]
) => {
  const dispatch = useDispatch()

  const workflowStatus = useSelector(selectWorkflowStatus)

  const singlesElRef = React.useRef<HTMLDivElement>(null)
  const multiElRef = React.useRef<HTMLDivElement>(null)

  const multiItems = items.filter(item => isSelectionAllowedInMulti(item.selection))
  const hasEnoughMultiItems = hasEnoughItemsToCreateValidMulti(multiItems)

  React.useEffect(() => {
    if (!containerElRef.current) return

    const ratioMap = new WeakMap<HTMLDivElement, number>()
    const setVisibleBettingSectionDebounced = debounce(
      (section: BettingSection) => dispatch(setVisibleBettingSection(section)),
      400
    )

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          ratioMap.set(entry.target as HTMLDivElement, entry.intersectionRatio)
        })

        const singlesRatio = getRatioValue(singlesElRef.current, ratioMap)
        const multiRatio = getRatioValue(multiElRef.current, ratioMap)
        setVisibleBettingSectionDebounced(singlesRatio > multiRatio ? 'Singles' : 'Multi')
      },
      { root: containerElRef.current, threshold: [0, 0.25, 0.5, 0.75, 1] }
    )

    if (singlesElRef.current) observer.observe(singlesElRef.current)
    if (multiElRef.current) observer.observe(multiElRef.current)

    return () => observer.disconnect()
    // Extra props used to reconnect observer after elements removed/added
  }, [dispatch, containerElRef, hasEnoughMultiItems, workflowStatus])

  return { singlesElRef, multiElRef }
}

// =============
// Local Helpers
// =============

function getRatioValue(
  el: HTMLDivElement | null,
  ratioMap: WeakMap<HTMLDivElement, number>
): number {
  return (el && ratioMap.get(el)) ?? 0
}
